import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { VersionDrawer, type VersionEntry } from '@shared/components/VersionDrawer/VersionDrawer';
import { componentsApi } from '@api/components';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { prettyPrintJson } from '../blocks/serialization';

interface ComponentVersionsDrawerProps {
  open: boolean;
  onClose: () => void;
  componentId: string;
  /** Geçerli anlık görüntü — componentJson varsa pretty-JSON, yoksa templateHtml (K-24). */
  currentSnapshot: string;
  onRestored: () => void;
}

/** Versiyon geçmişi çekmecesi — paylaşılan VersionDrawer'ı Component Studio'ya bağlar. */
export function ComponentVersionsDrawer({
  open,
  onClose,
  componentId,
  currentSnapshot,
  onRestored,
}: ComponentVersionsDrawerProps) {
  const { t } = useTranslation('component-studio');

  const { data: versions = [] } = useQuery({
    queryKey: ['component-versions', componentId],
    queryFn: () => componentsApi.versions(componentId),
    enabled: open && !!componentId,
  });

  // id → versionNumber (restore çağrısı için) + VersionDrawer girdi eşlemesi.
  const numberById = useMemo(
    () => new Map(versions.map((v) => [v.id, v.versionNumber])),
    [versions],
  );
  const entries = useMemo<VersionEntry[]>(
    () =>
      versions.map((v) => ({
        id: v.id,
        label: v.changeNote ? `v${v.versionNumber} — ${v.changeNote}` : `v${v.versionNumber}`,
        author: v.authorEmail || undefined,
        createdAt: v.createdAt ? dayjs(v.createdAt).format('DD.MM.YYYY HH:mm') : '',
        // K-24 — componentJson varsa pretty-JSON diff'i, yoksa legacy templateHtml.
        snapshot: v.componentJson ? prettyPrintJson(v.componentJson) : v.templateHtml,
      })),
    [versions],
  );

  const handleRestore = async (entry: VersionEntry) => {
    const versionNumber = numberById.get(entry.id);
    if (versionNumber == null) return;
    try {
      await componentsApi.restoreVersion(componentId, versionNumber);
      toast.success(t('toast.restored'));
      onClose();
      onRestored();
    } catch (err) {
      toast.error(resolveApiErrorMessage(err));
    }
  };

  return (
    <VersionDrawer
      open={open}
      onClose={onClose}
      versions={entries}
      currentContent={currentSnapshot}
      onRestore={handleRestore}
    />
  );
}
